/**
 * SchoolOverview — seat usage + class averages.
 * Great single-screen summary for the sales demo / school director.
 */
import React, { useState, useEffect } from 'react';
import { FiAlertCircle } from 'react-icons/fi';
import teacherAPI from '../services/api';

function daysSince(dateStr) {
  if (!dateStr) return Infinity;
  const d = new Date(dateStr);
  if (isNaN(d)) return Infinity;
  return Math.floor((Date.now() - d.getTime()) / 86400000);
}

function SchoolOverview({ setPageTitle, refreshKey }) {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [me, setMe] = useState(null);
  const [students, setStudents] = useState([]);

  useEffect(() => { setPageTitle('School Overview'); }, [setPageTitle]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError(null);
      try {
        const [meData, rosterData] = await Promise.all([
          teacherAPI.getMe(),
          teacherAPI.getStudents(),
        ]);
        if (!cancelled) {
          setMe(meData);
          setStudents(rosterData.students || []);
        }
      } catch (err) {
        if (!cancelled) setError(err.message);
      }
      if (!cancelled) setLoading(false);
    })();
    return () => { cancelled = true; };
  }, [refreshKey]);

  if (loading) {
    return <div className="loading" style={{ minHeight: '60vh' }}><div className="loading-spinner" /></div>;
  }

  if (error) {
    return (
      <div className="empty-state">
        <div className="empty-state-icon"><FiAlertCircle /></div>
        <div className="empty-state-title">Could not load school overview</div>
        <div className="empty-state-text">{error}</div>
      </div>
    );
  }

  const school = me?.school || {};
  const total = students.length;
  const activeWeek = students.filter((s) => (s.messagesThisWeek || 0) > 0).length;
  const activeRate = total ? Math.round((activeWeek / total) * 100) : 0;

  const accVals = students.map((s) => s.accuracy).filter((v) => v !== null && v !== undefined);
  const avgAccuracy = accVals.length ? Math.round(accVals.reduce((a, b) => a + b, 0) / accVals.length) : null;

  const avgLevel = total
    ? (students.reduce((a, s) => a + (s.level || 0), 0) / total).toFixed(1)
    : '0';

  const totalXP = students.reduce((a, s) => a + (s.xp || 0), 0);
  const totalVocab = students.reduce((a, s) => a + (s.vocabularyCount || 0), 0);
  const attention = students.filter(
    (s) => daysSince(s.lastActive) > 7 || (s.accuracy !== null && s.accuracy < 50)
  ).length;

  const seatLimit = school.seatLimit || total;
  const seatPct = seatLimit ? Math.round((total / seatLimit) * 100) : 0;

  return (
    <div>
      {/* Seat usage hero */}
      <div className="card" style={{ marginBottom: 24 }}>
        <div className="card-title">{school.name || 'Your School'}</div>
        <div style={{ display: 'flex', alignItems: 'baseline', gap: 12, marginTop: 8 }}>
          <span style={{ fontSize: 40, fontWeight: 700, fontFamily: "'Space Grotesk', sans-serif" }}>
            {total}
          </span>
          <span style={{ opacity: 0.5 }}>/ {seatLimit} seats used</span>
        </div>
        <div style={{
          height: 10, background: 'rgba(255,255,255,0.08)', borderRadius: 6,
          overflow: 'hidden', marginTop: 12,
        }}>
          <div style={{
            width: `${Math.min(100, seatPct)}%`, height: '100%',
            background: 'linear-gradient(135deg,#2997ff,#bf5af2)', borderRadius: 6,
          }} />
        </div>
        <div style={{ fontSize: 12, opacity: 0.5, marginTop: 6 }}>{seatPct}% of seats allocated</div>
      </div>

      {/* Class averages */}
      <div className="grid-3" style={{ marginBottom: 24 }}>
        <div className="stat-card">
          <div className="stat-card-label">Active This Week</div>
          <div className="stat-card-value">{activeWeek}<span style={{ fontSize: 16, opacity: 0.5 }}> / {total}</span></div>
          <div className="stat-card-label">{activeRate}% engagement</div>
        </div>
        <div className="stat-card">
          <div className="stat-card-label">Avg. Accuracy</div>
          <div className="stat-card-value" style={{
            color: avgAccuracy === null ? undefined
              : avgAccuracy >= 70 ? '#30d158'
              : avgAccuracy >= 50 ? '#ffb340' : '#ff6b6b',
          }}>
            {avgAccuracy === null ? '—' : `${avgAccuracy}%`}
          </div>
          <div className="stat-card-label">across all students</div>
        </div>
        <div className="stat-card">
          <div className="stat-card-label">Avg. Level</div>
          <div className="stat-card-value">{avgLevel}</div>
          <div className="stat-card-label">class proficiency</div>
        </div>
      </div>

      <div className="grid-3" style={{ marginBottom: 24 }}>
        <div className="stat-card">
          <div className="stat-card-label">Total XP Earned</div>
          <div className="stat-card-value">{totalXP.toLocaleString()}</div>
          <div className="stat-card-label">collective effort</div>
        </div>
        <div className="stat-card">
          <div className="stat-card-label">Words Learned</div>
          <div className="stat-card-value">{totalVocab.toLocaleString()}</div>
          <div className="stat-card-label">vocabulary across class</div>
        </div>
        <div className="stat-card">
          <div className="stat-card-label">Need Attention</div>
          <div className="stat-card-value" style={{ color: attention ? '#ff6b6b' : '#30d158' }}>
            {attention}
          </div>
          <div className="stat-card-label">inactive or struggling</div>
        </div>
      </div>

      <p style={{ fontSize: 13, opacity: 0.4, textAlign: 'center' }}>
        Powered by Hablamos · Real-time progress from your students' conversations with Carlos &amp; María
      </p>
    </div>
  );
}

export default SchoolOverview;
